import type { DocNode } from '@atlaskit/adf-schema';
import { Mark, Node, NodeType, Schema } from '@atlaskit/editor-prosemirror/model';

import type { JsonCloser } from './json-closer';

type ADFNode = {
	type: string;
	attrs?: Record<string, unknown>;
	content?: ADFNode[];
	marks?: Array<{
		type: string;
		attrs?: Record<string, unknown>;
	}>;
	text?: string;
};

// PartialNode's content can also be partial.
type PartialNode = Omit<ADFNode, 'type' | 'content'> & {
	type?: string;
	content?: PartialNode[];
};

export class ADFStreamer {
	private jsonCloser: JsonCloser;
	private schema: Schema;
	private lastDoc: DocNode;
	private lastStreamCompleteNodePath: number[] = [];

	constructor(schema: Schema, jsonCloser: JsonCloser) {
		this.jsonCloser = jsonCloser;
		this.schema = schema;
		const defaultDoc = schema.topNodeType.createAndFill()?.toJSON();
		this.lastDoc = { ...defaultDoc, version: 1 } as DocNode;
	}

	public processChunk(chunk: string): DocNode {
		this.jsonCloser.append(chunk);
		const [hasChanged, partialJson, stack, openSquareBracketsFor] = this.jsonCloser.parse();

		if (!partialJson || !hasChanged) {
			return this.createCopyOfDoc(this.lastDoc);
		}

		const completeADFJson = hasChanged ? this.parsePartialADFJson(partialJson) : this.lastDoc;

		this.lastStreamCompleteNodePath = this.getCompleteNodePath(
			partialJson,
			openSquareBracketsFor,
			stack,
		);

		return this.createCopyOfDoc(completeADFJson);
	}

	public getLastDoc(): DocNode {
		return this.createCopyOfDoc(this.lastDoc);
	}

	private createCopyOfDoc(doc: DocNode) {
		/**
		 * Some older browsers like chrome 97 doesn't support structuredClone.
		 * So using JSON.parse(JSON.stringify()) as a fallback.
		 */
		if (typeof window !== 'undefined' && typeof window.structuredClone === 'function') {
			return structuredClone(doc);
		} else {
			return JSON.parse(JSON.stringify(doc));
		}
	}

	private parsePartialADFJson(partialJson: PartialNode) {
		/**
		 * No need to wait till we get "type: doc" in the JSON.
		 * First node type will always be schema.topNodeType, and can start repairing
		 * 	the JSON as soon as there is content in it.
		 */
		try {
			this.lastDoc = this.repairNode({
				partialNodeJson: partialJson,
				// Top node is always at 0th Position.
				treeBranch: [0],
				partiallyRepairedNode: this.lastDoc as ADFNode,
				isTopNode: true,
			}).toJSON() as DocNode;
		} catch (e) {
			//TODO: EDITOR-783 - Fire analytic event to track this error
			// eslint-disable-next-line no-console
			console.error('Failed to repair partial JSON', e);
		}

		return this.lastDoc;
	}

	private repairNode({
		partialNodeJson,
		treeBranch,
		partiallyRepairedNode,
		parentNodeType,
		isTopNode,
	}: {
		partialNodeJson: PartialNode;
		treeBranch: number[];
		partiallyRepairedNode?: ADFNode;
		parentNodeType?: NodeType;
		isTopNode?: boolean;
	}): Node {
		/**
		 * If type is still not available, and, node has been previsously repaired,
		 * 	then just use type of the repaired node.
		 * And if node hasn't been repaired, then use default type based on parent node type.
		 */
		const type =
			(partialNodeJson.type && this.schema.nodes[partialNodeJson.type]) ||
			(isTopNode
				? this.schema.topNodeType
				: (partiallyRepairedNode?.type && this.schema.nodes[partiallyRepairedNode.type]) ||
					parentNodeType?.contentMatch.defaultType);

		if (!type) {
			//TODO: EDITOR-783 - Fire analytic event to track this error.
			throw new Error('Failed to repair node, type not found');
		}

		const maybeMarks = partialNodeJson.marks
			? partialNodeJson.marks
					.map((mark) => this.repairMark(mark))
					// Type guard isn't necessary in newer versions of TypeScript
					.filter((mark: Mark | null): mark is Mark => mark !== null)
			: [];

		if (type.name === 'text') {
			/**
			 * Empty text node is not allowed in prosemirror.
			 * Checkout: https://prosemirror.net/docs/ref/#model.Schema.text
			 * That's why when text is not available, we are using space as default.
			 */
			return this.schema.text(partialNodeJson.text ? partialNodeJson.text : ' ', maybeMarks);
		}

		const maybeContent = partialNodeJson.content
			? this.repairContent({
					repairedContent: partiallyRepairedNode?.content || [],
					newContent: partialNodeJson.content,
					treeBranch,
					parentNodeType: type,
				}).map((node) => this.schema.nodeFromJSON(node))
			: null;

		const node = type.createAndFill(partialNodeJson.attrs, maybeContent, maybeMarks);

		if (!node) {
			//TODO: EDITOR-783 - Fire analytic event to track this error
			throw new Error('Failed to repair node');
		}
		return node;
	}

	private repairContent({
		repairedContent,
		newContent,
		parentNodeType,
		treeBranch,
	}: {
		repairedContent: ADFNode[];
		newContent: PartialNode[];
		// Represents node level of content nodes
		treeBranch: number[];
		parentNodeType?: NodeType;
	}): ADFNode[] {
		const repairedContentLength = repairedContent.length;

		let previouslyRepairedNodes: ADFNode[] = [];
		let completelyRepairedNode: ADFNode | undefined;
		if (repairedContentLength > 0 && newContent.length > 0) {
			/**
			 * Complete ADF JSON for these nodes has been received.
			 * They have been fully reparied.
			 */
			previouslyRepairedNodes = repairedContent.slice(0, repairedContentLength - 1);

			const mayBePartiallyRepairedNode = repairedContent[repairedContentLength - 1];
			const partialNodeTreeBranch = [...treeBranch, repairedContentLength - 1];
			if (mayBePartiallyRepairedNode) {
				if (this.isStreamingOfNodeComplete(partialNodeTreeBranch)) {
					completelyRepairedNode = mayBePartiallyRepairedNode;
				} else {
					completelyRepairedNode = this.repairNode({
						partialNodeJson: newContent[repairedContentLength - 1],
						parentNodeType,
						treeBranch: partialNodeTreeBranch,
						partiallyRepairedNode: mayBePartiallyRepairedNode,
					}).toJSON();
				}
			}
		}

		/**
		 * Lastest stream can complete previous node and also add new nodes.
		 * New nodes are not repaired yet, so don't have partiallyRepairedNode
		 */
		const newRepairedContentNodes = newContent.slice(repairedContentLength).map((node, index) => {
			return this.repairNode({
				partialNodeJson: node,
				treeBranch: [...treeBranch, repairedContentLength + index],
				parentNodeType,
			}).toJSON();
		});

		// completelyRepairedNode will be undefined when only one node is in content, so filtering it.
		return [...previouslyRepairedNodes, completelyRepairedNode, ...newRepairedContentNodes].filter(
			(node) => !!node,
		);
	}

	private isStreamingOfNodeComplete(treeBranch: number[]) {
		// Streaming is just started and no node has been completed yet.
		if (this.lastStreamCompleteNodePath.length === 0) {
			return false;
		}

		/**
		 * If node is in left side of last complete tree branch then it is repaired in last stream.
		 * And if it is in right side then it is not repaired yet.
		 */
		for (let i = 0; i < treeBranch.length; i++) {
			if (treeBranch[i] < this.lastStreamCompleteNodePath[i]) {
				return true;
			} else if (treeBranch[i] > this.lastStreamCompleteNodePath[i]) {
				return false;
			}
		}
		/**
		 * If it reaches to this point then;
		 * - current node is is in last complete tree branch, but that it's one of the parent node is still incomplete.
		 * In this case, treeBranch length is greater than last complete node path length.
		 * And length of both branch is same then it is the same node which was completed in last stream.
		 */
		return treeBranch.length >= this.lastStreamCompleteNodePath.length;
	}

	private getCompleteNodePath(
		partialJson: PartialNode,
		openSquareBracketsFor: Array<'content' | 'other'>,
		stack: string[],
	) {
		const numberOfOpenCurlyBrackets = stack.filter((bracket) => bracket === '{').length;
		const numberOfOpenContentBrackets = openSquareBracketsFor.filter(
			(bracketFor) => bracketFor === 'content',
		).length;

		/**
		 * If there are no open content square brackets then it means that
		 * 	- either last received node is complete, or
		 *  - doc node content isn't received yet.
		 */
		if (numberOfOpenContentBrackets === 0) {
			return [];
		}

		const currentRightMostTreeBranch = this.getRightMostTreeBranch(partialJson);

		/**
		 * Ideally this should never happen, but just in case if it happens then
		 * 	then return empty array so whole new json will be reparied instead of
		 *  throwing error becuase this is for optimization, new json can stil be
		 *  valid and can be reparied.
		 */
		if (numberOfOpenContentBrackets > currentRightMostTreeBranch.length) {
			//TODO: EDITOR-783 - Fire analytic event to track this case.
			return [];
		}

		/**
		 * Same open content square brackets as length of right most tree branch means, last received node is incomplete.
		 * So complete branch is just one position left to current right most tree branch.
		 */
		if (numberOfOpenContentBrackets === currentRightMostTreeBranch.length) {
			for (let i = currentRightMostTreeBranch.length - 1; i >= 0; i--) {
				if (currentRightMostTreeBranch[i] > 0) {
					currentRightMostTreeBranch[i] -= 1;
					break;
				}
				currentRightMostTreeBranch.pop();
			}
			return currentRightMostTreeBranch;
		}

		/**
		 * When right most branch length is just one more than number of open content square brackets,
		 * 	then it means that last node having content is incomplete and
		 * 	one or more children of that node may be complete.
		 */
		if (currentRightMostTreeBranch.length === numberOfOpenContentBrackets + 1) {
			/**
			 * If number of open curly brackets is same as number of open content square brackets
			 *  then it means all children of last open content node are complete.
			 * So current right most tree branch is complete node path.
			 */
			if (numberOfOpenCurlyBrackets === numberOfOpenContentBrackets) {
				return currentRightMostTreeBranch;
			} else if (currentRightMostTreeBranch[currentRightMostTreeBranch.length - 1] > 0) {
				/**
				 * If it come here then it means;
				 * - open curly brackets are more than open content square brackets
				 * - last open content node has more than one children and
				 * 	last child is incomplete.
				 * So just decrement last child index of current right most tree branch
				 * 	to get complete node path.
				 */
				currentRightMostTreeBranch[currentRightMostTreeBranch.length - 1] -= 1;
				return currentRightMostTreeBranch;
			} else {
				/**
				 * If it come here then it means;
				 * - open curly brackets are more than open content square brackets
				 * - last open content node has only one child which is incomplete.
				 * So complete branch is just one position left to current right most tree branch
				 */
				for (let i = currentRightMostTreeBranch.length - 1; i >= 0; i--) {
					if (currentRightMostTreeBranch[i] > 0) {
						currentRightMostTreeBranch[i] -= 1;
						break;
					}
					currentRightMostTreeBranch.pop();
				}
				return currentRightMostTreeBranch;
			}
		}
		/**
		 * Number of open content square brackets means that, that many nodes from top are incomplete.
		 * So last complete node will be last content node of last incomplete node (whose content square bracket is still open).
		 */
		return currentRightMostTreeBranch.slice(0, numberOfOpenContentBrackets + 1);
	}

	private getRightMostTreeBranch(partialJson: PartialNode) {
		// Top node is always at 0th Position.
		const branch: number[] = [0];

		function setIncompleteTreeBranchForNode(node: PartialNode) {
			if (node.content && Array.isArray(node.content) && node.content.length > 0) {
				branch.push(node.content.length - 1);
				setIncompleteTreeBranchForNode(node.content[node.content.length - 1]);
			}
		}
		setIncompleteTreeBranchForNode(partialJson);

		return branch;
	}

	private repairMark(json: PartialNode): Mark | null {
		if (json.type) {
			const type = this.schema.marks[json.type];
			if (!type) {
				throw new Error('Failed to repair mark');
			}
			return type.create(json.attrs);
		}
		return null;
	}
}
